import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { EDITOR_ROLES } from "@shared/core/types";
import {
  CANDIDATE_STATUSES,
  CANDIDATE_STATUS_LABELS,
  EDUCATION_LABELS,
  EDUCATION_LEVELS,
  SEXES,
  SEX_LABELS,
  type CandidatePage,
} from "@shared/modules/x-candidats";
import { useAuthStore } from "../../../core/stores/auth";
import { useToastStore } from "../../../core/stores/toast";
import { PageHeader } from "../../../core/components/ui/page-header";
import { Button } from "../../../core/components/ui/button";
import { Field, Input, Select } from "../../../core/components/ui/input";
import { Icon } from "../../../core/components/ui/icon";
import { formatDateTime } from "../../../core/lib/format";
import { allowedFor } from "../../../core/modules";
import { listCandidates } from "../lib/api";
import { StatusBadge } from "../components/status-badge";

const EMPTY = { q: "", status: "", sex: "", profession: "", country: "", educationLevel: "", ageMin: "", ageMax: "", cv: "" };

export function CandidatesList() {
  const user = useAuthStore((s) => s.user);
  const push = useToastStore((s) => s.push);
  const navigate = useNavigate();
  const [filters, setFilters] = useState(EMPTY);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<CandidatePage | null>(null);
  const [busy, setBusy] = useState(false);
  const canEdit = allowedFor({ roles: EDITOR_ROLES }, user?.role);
  const isLawyer = user?.role === "avocat";

  const load = async (p = page) => {
    setBusy(true);
    try {
      setData(await listCandidates({ ...filters, page: p, pageSize: 25 } as Record<string, string | number>));
    } catch {
      push("danger", "Impossible de charger les dossiers");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k: keyof typeof EMPTY) => (e: { target: { value: string } }) => setFilters({ ...filters, [k]: e.target.value });
  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <div className="page">
      <PageHeader
        title={isLawyer ? "Dossiers qui vous sont confiés" : "Dossiers candidats"}
        subtitle={data ? `${data.total} dossier${data.total > 1 ? "s" : ""}` : undefined}
        actions={
          canEdit ? (
            <Button variant="primary" icon={<Icon name="plus" size={16} />} onClick={() => navigate("/candidats/nouveau")}>
              Nouveau dossier
            </Button>
          ) : undefined
        }
      />

      <form
        className="candidats-filters"
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          void load(1);
        }}
      >
        <Field label="Recherche" hint="Nom, référence, courriel, profession, notes…">
          <Input value={filters.q} onChange={set("q")} placeholder="ex. Diallo, C-00012, soudeur" />
        </Field>
        <Field label="Dans le contenu des CV">
          <Input value={filters.cv} onChange={set("cv")} placeholder="ex. chantier naval" />
        </Field>
        <Field label="Statut">
          <Select value={filters.status} onChange={set("status")}>
            <option value="">Tous</option>
            {CANDIDATE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {CANDIDATE_STATUS_LABELS[s]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Sexe">
          <Select value={filters.sex} onChange={set("sex")}>
            <option value="">Tous</option>
            {SEXES.map((s) => (
              <option key={s} value={s}>
                {SEX_LABELS[s]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Profession">
          <Input value={filters.profession} onChange={set("profession")} />
        </Field>
        <Field label="Pays (nationalité ou résidence)">
          <Input value={filters.country} onChange={set("country")} />
        </Field>
        <Field label="Niveau d'études">
          <Select value={filters.educationLevel} onChange={set("educationLevel")}>
            <option value="">Tous</option>
            {EDUCATION_LEVELS.map((l) => (
              <option key={l} value={l}>
                {EDUCATION_LABELS[l]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Âge minimum">
          <Input type="number" min={0} max={120} value={filters.ageMin} onChange={set("ageMin")} />
        </Field>
        <Field label="Âge maximum">
          <Input type="number" min={0} max={120} value={filters.ageMax} onChange={set("ageMax")} />
        </Field>
        <div className="candidats-filters__actions">
          <Button type="submit" variant="primary" disabled={busy} icon={<Icon name="search" size={16} />}>
            Rechercher
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setFilters(EMPTY);
              setPage(1);
              setTimeout(() => void load(1), 0);
            }}
          >
            Effacer
          </Button>
        </div>
      </form>

      <table className="table">
        <thead>
          <tr>
            <th>Référence</th>
            <th>Candidat</th>
            <th>Profession</th>
            <th className="table__col--wide">Pays</th>
            <th className="table__col--wide">Âge</th>
            <th>Statut</th>
            <th className="table__col--wide">Mis à jour</th>
          </tr>
        </thead>
        <tbody>
          {(data?.items ?? []).map((c) => (
            <tr key={c.id} className="table__row--link" onClick={() => navigate(`/candidats/${c.id}`)}>
              <td>
                <Link to={`/candidats/${c.id}`}>{c.reference}</Link>
              </td>
              <td>
                {c.lastName.toUpperCase()} {c.firstName}
              </td>
              <td>{c.profession ?? "—"}</td>
              <td className="table__col--wide">{c.countryResidence ?? c.nationality ?? "—"}</td>
              <td className="table__col--wide">{c.age ?? "—"}</td>
              <td>
                <StatusBadge status={c.status} />
              </td>
              <td className="table__col--wide">{formatDateTime(c.updatedAt, { dateStyle: "medium" })}</td>
            </tr>
          ))}
          {data && data.items.length === 0 ? (
            <tr>
              <td colSpan={7} className="table__empty">
                Aucun dossier ne correspond.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>

      {pages > 1 ? (
        <div className="candidats-pagination">
          <Button variant="ghost" size="sm" disabled={page <= 1 || busy} onClick={() => { setPage(page - 1); void load(page - 1); }}>
            Précédent
          </Button>
          <span className="muted">
            Page {page} / {pages}
          </span>
          <Button variant="ghost" size="sm" disabled={page >= pages || busy} onClick={() => { setPage(page + 1); void load(page + 1); }}>
            Suivant
          </Button>
        </div>
      ) : null}
    </div>
  );
}
